import {
	FilterItem,
	FilterItemPascal,
	KeyedArray,
	keyPair,
	mapToKeyedArray,
	mapToKeyedArrayPascal,
} from "@repo/models";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export const BRANCHES = "branches";
export const CHANNELS = "channels";
export const BRANDS = "brands";
export const MARKETERS = "marketers";
export const STATES = "states";

export const COMPANIES = "companies";
export const DIVISIONS = "divisions";
export const REGIONS = "regions";
export const MARKETS = "markets";

export const VERBIAGE = "verbiage";

export const GROUP = {
	LOCATION: "Location",
	LOCATIONS: "Locations",
	ALL_LOCATIONS: "AllLocations",
};

export const LOCATION = {
	DETAILS: [GROUP.LOCATION],
	ASSOCIATIONS: [GROUP.LOCATION, "associations"],
	STATUS_HISTORIES: [GROUP.LOCATION, "statusHistories"],
	ACTION_BUTTONS: [GROUP.LOCATION, "actionButtons"],
};

export const PORTAL = {
	BRANCHES: [GROUP.LOCATION, "branches"],
	BRANDS: [GROUP.LOCATION, "brands"],
	CHANNELS: [GROUP.LOCATION, "channels"],
	COMPANY_DIVISIONS: [GROUP.LOCATION, "companyDivisions"],
	REPS: [GROUP.LOCATION, "reps"],
	MARKETERS: [GROUP.LOCATION, "marketers"],
	STATES: [GROUP.LOCATION, "states"],
	COUNTRIES: [GROUP.LOCATION, "countries"],
	LOCATION_STATUSES: [GROUP.LOCATION, "locationStatuses"],
};

export const STATES_BY_COUNTRY = "statesByCountry";
export const RETAIL_PARTNER_TYPES = "retailPartnerTypes";
export const RETAIL_PARTNERS = "retailPartners";
export const ALL_BRANCHES = "allBranches";

export const SETTINGS = "settings";
export const DYNAMIC_COMPONENTS = "dynamicComponents";

export const REP_FIELD_AUDITS = ["RepFieldAudits"];
export const REP_FIELD_AUDIT_QUESTIONS = ["RepFieldAuditQuestions"];
export const REP_FIELD_AUDIT_FORM_TYPES = ["RepFieldAuditFormTypes"];
export const REP_FIELD_AUDIT_QUESTION_OPTIONS = ["RepFieldAuditQuestionOptions"];
export const REP_FIELD_AUDIT_COMPLAINT_TYPES = ["RepFieldAuditComplaintTypes"];
export const REP_FIELD_AUDIT_LOCATIONS = ["RepFieldAuditLocations"];
export const REP_FIELD_AUDIT_ALL_HISTORIES = ["RepFieldAuditAllHistories"];

export function useFilterItemQuery<T extends FilterItem = FilterItem>(
	name: string[],
	api: (...params) => Promise<(T | FilterItem)[]>,
	...params
) {
	const query = useQuery({ queryKey: [...name, ...params], queryFn: callApi });
	const [data, setData] = useState(query.data ?? emptyArray);
	const [loaded, setLoaded] = useState(query.isFetched);

	useEffect(() => {
		if (query.isFetched) setData(query.data);
		if (query.isFetched) setLoaded(true);
	}, [query.isFetched, query.data]);
	return [data, loaded] as [KeyedArray<T>, boolean];

	async function callApi() {
		return await api(...params).then(mapToKeyedArray);
	}
}

export function useFilterItemPascalQuery<T extends FilterItemPascal = FilterItemPascal>(
	name: string[],
	api: (...params) => Promise<(T | FilterItemPascal)[]>,
	...params
) {
	const query = useQuery({ queryKey: [...name, ...params], queryFn: callApi });
	const [data, setData] = useState(query.data ?? emptyArray);
	const [loaded, setLoaded] = useState(query.isFetched);

	useEffect(() => {
		if (query.isFetched) setData(query.data);
		if (query.isFetched) setLoaded(true);
	}, [query.isFetched, query.data]);
	return [data, loaded] as [KeyedArray<T>, boolean];

	async function callApi() {
		return await api(...params).then(mapToKeyedArrayPascal);
	}
}

export function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
	if (!loaded) return null as keyPair[];
	if (forceInclude) allItems.set(forceInclude);
	if (!allItems) return [];
	return allItems.map((c) => {
		return { id: c.id, value: c.name ?? c.value } as keyPair;
	});
}

export function getItemsPascal(
	allItems: KeyedArray<FilterItemPascal>,
	loaded: boolean,
	forceInclude?: FilterItemPascal,
) {
	if (!loaded) return null as keyPair[];
	if (forceInclude) allItems.set(forceInclude);
	if (!allItems) return [];
	return allItems.map((c) => {
		return { id: c.Id, value: c.Name } as keyPair;
	});
}

const emptyArray = new KeyedArray<FilterItemPascal>([], (k) => k.Id);
